import { DataTableActionBarAction } from "@/components/data-table/data-table-action-bar"
import { ProcessorSelector } from "@/components/form";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTitle, DialogContent, DialogHeader, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useChangeBulkSubscriptionProcessor } from "@/lib/api/subscriptions";
import { Loader2, Settings2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";


interface ChangeProcessorBulkActionModalProps {
    subscriptionIds: string[];
    size?: 'sm' | 'md';
}

export const ChangeProcessorBulkActionModal = ({ subscriptionIds, size = 'sm' }: ChangeProcessorBulkActionModalProps) => {
    const { t } = useTranslation('subscriptions');
    const { mutate: changeProcessorMutation, isPending: isChangingProcessor } = useChangeBulkSubscriptionProcessor();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedProcessorId, setSelectedProcessorId] = useState<string | undefined>(undefined);

    const handleSubmit = () => {
        if (!selectedProcessorId) return;

        changeProcessorMutation({ subscriptionIds, processorId: selectedProcessorId }, {
            onSettled: () => {
                setIsOpen(false);
            },
        });
    };

    const handleOpenChange = (open: boolean) => {
        if (!open && !isChangingProcessor) setIsOpen(false);
    };
    return (
        <>

            {size === 'sm' && (
                <DataTableActionBarAction
                    tooltip={t('bulkActions.changeProcessor.tooltip')}
                    isPending={isChangingProcessor}
                    onClick={() => setIsOpen(true)}
                >
                    <Settings2 className="mr-2 h-4 w-4" />
                    {t('bulkActions.changeProcessor.button')}
                </DataTableActionBarAction>
            )}
            {size === 'md' && (
                <Button variant="outline" size="sm" onClick={() => setIsOpen(true)}>
                    <Settings2 className="mr-2 h-4 w-4" />
                    {t('bulkActions.changeProcessor.button')}
                </Button>
            )}
            <Dialog open={isOpen} onOpenChange={handleOpenChange}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>{t('bulkActions.changeProcessor.title')}</DialogTitle>
                        <DialogDescription>
                            {t('bulkActions.changeProcessor.confirmMessage', { count: subscriptionIds.length })}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="processor-selector">
                                {t('bulkActions.changeProcessor.selectProcessor')}
                            </Label>
                            <ProcessorSelector
                                processorId={selectedProcessorId}
                                onProcessorChange={setSelectedProcessorId}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setIsOpen(false)} variant="outline">
                            {t('bulkActions.changeProcessor.cancel')}
                        </Button>
                        <Button onClick={handleSubmit} disabled={isChangingProcessor || !selectedProcessorId}>
                            {isChangingProcessor && <Loader2 className="size-4 animate-spin mr-2" />}
                            {t('bulkActions.changeProcessor.change')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}